<template>
  <div class="group-list">
    <template v-if="listLoading">
      <div class="list-loading">
        <label>{{ i18n.global.t('pages.common.list.reading') }}</label>
      </div>
    </template>
    <template v-else-if="!isSearchCompleted && isManagementOffice">
      <div class="list-loading">
        <label>{{ i18n.global.t('pages.common.list.runFilter') }}</label>
      </div>
    </template>
    <ResizableList
      scrollAreaSelector=".list"
    >
      <div class="list list-scrollbar">
        <v-table>
          <thead>
            <tr class="table-header-row">
              <th
                v-for="(item, index) in header"
                :key="index"
                class="table-header column-space"
                :class="applyLeftClass(index)"
              >
                <div class="d-flex">
                  <span v-if="sortableItem(item)" class="d-inline-block">
                    <div
                      class="nc-icon nc-stre-up sort-icon d-block"
                      @click="emitChangeSortOrder(item, sortTypes[0])"
                      :class="highLightIfCurrentSort(item, sortTypes[0])"
                    />
                    <div
                      class="nc-icon nc-stre-down sort-icon d-block"
                      @click="emitChangeSortOrder(item, sortTypes[1])"
                      :class="highLightIfCurrentSort(item, sortTypes[1])"
                    />
                  </span>
                  <span
                    :class="addClassIfSortable(item)"
                    @click="emitChangeSortOrder(item)"
                    >{{ item }}</span
                  >
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="groups?.length != 0">
              <tr v-for="group in groups" :key="group.id">
                <td class="column-space fix-td">{{ group.id }}</td>
                <td class="column-space">
                  {{ group.operation_office.operation_office_name }}
                </td>
                <td class="column-space">{{ group.group_name }}</td>
                <td class="column-space">{{ group.is_comment }}</td>
                <td class="column-space">
                  <StatusChangeConfirmModal
                    :item="group"
                    :updatableStatusButton="changeStatusAuthority"
                    :updatingStatus="updatingStatus"
                    @submit="emitChangeStatus"
                  />
                </td>
                <td class="column-space">{{ datetimeFormat(group.updated_at) }}</td>
                <td class="column-space">
                  <DisplayFullnameFromUpdatedBy
                    :updatedBy="group.updated_by"
                  />
                </td>
                <td class="column-space">
                  <EditLinkIcon :name="'GroupForm'" :id="group.id" />
                </td>
              </tr>
            </template>
            <template v-else-if="groups?.length == 0 && !listLoading && isSearchCompleted">
              <tr>
                <td colspan="9">
                  {{ i18n.global.t('pages.common.list.noData') }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </div>
    </ResizableList>
  </div>
</template>

<script>
import StatusChangeConfirmModal from "@/components/GroupParts/StatusChangeConfirmModal";
import ResizableList from '@/components/ResizableList.vue';
import EditLinkIcon from "@/components/Common/EditLinkIcon";
import DisplayFullnameFromUpdatedBy from '@/components/Common/DisplayFullnameFromUpdatedBy.vue';
import i18n from '@/lang/i18n';

export default {
  setup() {
    return {
      i18n
    }
  },
  props: {
    groups: [],
    changeStatusAuthority: {
      type: Boolean,
      default: false,
    },
    listLoading: {
      type: Boolean,
      default: true,
    },
    isSearchCompleted: {
      type: Boolean,
      default: false,
    },
    updatingStatus: {
      type: Boolean,
      default: false,
    },
    isManagementOffice: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      header: [
        i18n.global.t('pages.groups.list.id'),
        i18n.global.t('pages.groups.list.operationOfficeName'),
        i18n.global.t('pages.groups.groupName'),
        i18n.global.t('pages.groups.comment'),
        i18n.global.t('pages.groups.list.status'),
        i18n.global.t('pages.groups.list.updatedAt'),
        i18n.global.t('pages.groups.list.updatedBy'),
        i18n.global.t('pages.groups.list.edit'),
      ],
      sortableItems: {
        [i18n.global.t('pages.groups.list.id')]: "id",
        [i18n.global.t('pages.groups.list.operationOfficeName')]: "operation_office",
        [i18n.global.t('pages.groups.groupName')]: "group_name",
        [i18n.global.t('pages.groups.list.status')]: "status",
        [i18n.global.t('pages.groups.list.updatedAt')]: "updated_at",
        [i18n.global.t('pages.groups.list.updatedBy')]: "updated_by",
      },
      currentSort: { item: "group_name", type: "ASC" },
      sortTypes: ["ASC", "DESC"],
    };
  },
  methods: {
    datetimeFormat(datetime) {
      const d = new Date(datetime);
      const year = d.getFullYear();
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      const day = d.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    officeableTypeName(officeableType) {
      const type = officeableType.split("\\")[2];
      return this.officeableTypeNames[type];
    },
    officeableName(group) {
      const type = group.officeable_type.split("\\")[2];
      if (type == "OperationOffice") {
        return group.officeable.operation_office_name;
      } else {
        return group.officeable.company_name;
      }
    },
    emitChangeSortOrder(sortItem, sortType = null) {
      if (!this.sortableItem(sortItem)) return;

      const item = this.sortableItems[sortItem];
      if (sortType) {
        this.currentSort.type = sortType;
      } else {
        if (this.currentSort.item == item) {
          const type = this.sortTypes.find(
            (type) => type != this.currentSort.type
          );
          this.currentSort.type = type;
        } else {
          this.currentSort.type = this.sortTypes[0];
        }
      }
      this.currentSort.item = item;
      this.$emit(
        "changeSortOrder",
        this.currentSort.item,
        this.currentSort.type
      );
    },
    sortableItem(itemName) {
      return Object.keys(this.sortableItems).includes(itemName);
    },
    addClassIfSortable(itemName) {
      if (!this.sortableItem(itemName)) return;

      return "sort-icon pl-2 pt-1";
    },
    highLightIfCurrentSort(item, type) {
      if (
        this.currentSort.item != this.sortableItems[item] ||
        this.currentSort.type != type
      )
        return;

      return "fw-bold";
    },
    applyLeftClass(index) {
      if (index == 0) return "start-0";
    },
    emitChangeStatus(params) {
      this.$emit("changeStatus", params);
    }
  },
  components: {
    ResizableList,
    StatusChangeConfirmModal,
    EditLinkIcon,
    DisplayFullnameFromUpdatedBy
  },
};
</script>

<style>
.group-list .list table {
  position: absolute;
}
.group-list .list td {
  word-break: keep-all;
}
.group-list .list th {
  min-width: 7em;
  position: -webkit-sticky;
  position: sticky;
  background-color: white;
  top: 0;
  z-index: 1;
}
</style>
<style scoped>
.group-list {
  position: relative;
}
.group-list .list {
  position: relative;
  min-width: 58vw;
  height: var(--resizable-height, var(--list-default-height));
  overflow: auto;
}
.group-list table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
}
.group-list .list .id-column {
  min-width: 2em;
}
.group-list .list .updated-at-column {
  min-width: 10em;
}
.checkbox-in-grid {
  padding: 1vm;
  height: inherit !important;
  display: inline-grid !important;
}
.list-loading {
  position: absolute;
  min-width: 58vw;
  height: 100%;
  overflow: auto;
  z-index: 1000;
  background-color: rgba(80,80,80,0.5);
  right: 0;
  left: 0;
}
.list-loading>label {
  font-weight: bold;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}
.column-space {
  white-space: nowrap;
}
.group-list .sort-icon:hover {
  cursor: pointer;
  opacity: 0.5;
}
</style>
